/**
 * "Why?" (R10, Doom baseline 10-20): for one team of one Penelope row, every reason she has — for a working order what it
 * is for, when it went in (Pacific time), what the placing decision saw, and whether it is kept, moving or coming off;
 * for an empty side what is stopping it, the touch she judged against, and every hold. Pure `whyLines()` plus a small
 * popover, opened from the order chips, a "?" beside each book on Game Detail, the ticket's status and the Desk.
 * Display only: it reads the row's snapshot and sends nothing.
 */
import { useState, type ReactNode } from 'react';
import type { PenelopeGame, PenelopeLive, PenelopeRest } from '@/hooks/usePenelope';
import { usePenelopeAccess } from '@/hooks/usePenelopeAccess';
import { usePenelopeSides } from '@/hooks/usePenelopeSides';
import { ptClock } from '@/lib/infra/ptTime';
import { cn } from '@/lib/infra/utils';
import type { GameRow } from '@/views/ValueDashboard/types';
import { blockerText, keptText, pacingText, purposeOf } from './penelopeStatus';

type Team = 'A' | 'B';
type Snapshot = PenelopeLive['baseline'];

export interface WhyInput {
  live: Snapshot;
  team: Team;
  teamA: string;
  teamB: string;
  /** Which of the side's orders (default: main, else aux). */
  slot?: 'main' | 'aux';
  /** Or the order itself, wherever it rests. */
  orderId?: string;
  configVersion?: number | null;
}

const secs = (ms: number): string => (ms < 10_000 ? `${(ms / 1000).toFixed(1)} s` : `${Math.round(ms / 1000)} s`);

/** The order `i` asks about, and its slot, or null. */
function orderOf(i: WhyInput): { rest: PenelopeRest; slot: 'main' | 'aux' } | null {
  const main = i.live.rests[i.team];
  const aux = i.live.aux?.[i.team] ?? null;
  if (i.orderId) {
    if (main?.orderId === i.orderId) return { rest: main, slot: 'main' };
    if (aux?.orderId === i.orderId) return { rest: aux, slot: 'aux' };
    return null;
  }
  if (i.slot === 'aux') return aux ? { rest: aux, slot: 'aux' } : null;
  if (main) return { rest: main, slot: 'main' };
  return aux ? { rest: aux, slot: 'aux' } : null;
}

/** Every line "Why?" shows for one team, most important first. */
export function whyLines(i: WhyInput): string[] {
  const { live, team } = i;
  const name = team === 'A' ? i.teamA : i.teamB;
  const other = team === 'A' ? i.teamB : i.teamA;
  const reducing = live.reducing?.[team] ?? false;
  const side = live.sides?.[team] ?? null;
  const out: string[] = [];
  const found = orderOf(i);
  if (found) {
    const { rest: r, slot } = found;
    const want = slot === 'aux' ? live.auxWant?.[team] : live.want?.[team];
    const decided = slot === 'aux' ? live.auxWhy?.[team] : live.desiredWhy?.[team];
    const left = r.placed !== undefined && r.placed > r.size ? `×${r.size} of ${r.placed}` : `×${r.size}`;
    const purpose = purposeOf(r.why, other, reducing).replace(/^ \((.*)\)$/, '$1');
    out.push(`${name} ${r.px}¢ ${left}${purpose ? ` — ${purpose}` : ''}${slot === 'aux' ? ' (second slot)' : ''}`);
    if (r.placedAtMs) out.push(`Placed ${ptClock(r.placedAtMs)}${r.ageMs !== undefined ? `, ${secs(r.ageMs)} ago` : ''}${r.regime ? `, in ${r.regime}` : ''}`);
    if (typeof r.edgeC === 'number' && typeof r.fairC === 'number') out.push(`When placed: edge ${r.edgeC.toFixed(1)}¢ against Pinnacle fair ${r.fairC.toFixed(1)}¢`);
    if (r.why) out.push(`Her reason: ${r.why}`);
    if (want?.kind === 'quote' && want.px !== null && want.px !== r.px) out.push(`Now: moving to ${want.px}¢`);
    else if (want?.kind === 'none') out.push(`Now: coming off — ${blockerText(decided, live.gate) ?? 'the rule that placed it no longer holds'}`);
    else if (want?.kind === 'keep') out.push(`Now: kept${keptText(decided) ? ` — ${keptText(decided)}` : ''}`);
  } else {
    const want = live.want?.[team];
    out.push(`${name}: nothing resting`);
    if (want?.kind === 'quote' && want.px !== null) out.push(`Now: about to post at ${want.px}¢`);
    else {
      const why = blockerText(live.desiredWhy?.[team], live.gate) ?? (live.gate ? live.gate : null);
      if (why) out.push(`Not posting: ${why}`);
    }
  }
  if (side) {
    if (side.raw !== null) out.push(side.thin ? `Best bid ${side.raw}¢ is thin — she judges against ${side.touch === null ? 'no level' : `${side.touch}¢`} (min contracts at the best bid)` : `Best bid ${side.raw}¢`);
    else out.push('No best bid on this side');
    out.push(side.entry ? `Buying ${name} here ADDS to her position${reducing ? ' (an entry on Pinnacle value, though she is long the other team)' : ''}` : `Buying ${name} here REDUCES what she holds (sells ${other} back)`);
    if (side.unknownHoldMs > 0) out.push(`Waiting ${secs(side.unknownHoldMs)} for Kalshi to confirm an earlier order on this side`);
  }
  const venue = live.venueHold?.[team];
  if (venue) out.push(`The venue check held her last order here: ${venue}`);
  if (live.heldBack) out.push(`Held back last pass: ${pacingText(live.heldBack)}`);
  if (live.liquidityGate) out.push(`Book: ${live.liquidityGate}`);
  if (live.scanNote) out.push(`Scanner: ${live.scanNote}`);
  const v = i.configVersion ?? live.configVersion;
  if (v) out.push(`Settings v${v} · ${live.regime}${live.regimeAgeMs !== undefined ? ` for ${secs(live.regimeAgeMs)}` : ''}`);
  return out;
}

/** The popover body. */
function WhyPanel({ lines, onClose }: { lines: string[]; onClose: () => void }) {
  return (
    <div role="dialog" aria-label="Why" className="border-border bg-background absolute right-0 top-full z-40 mt-1 w-80 max-w-[90vw] rounded border p-2 text-left text-[11px] font-normal normal-case shadow-lg">
      <div className="mb-1 flex items-center justify-between">
        <span className="text-foreground font-semibold">Why</span>
        <button type="button" className="text-muted-foreground hover:text-foreground px-1" aria-label="Close" onClick={onClose}>×</button>
      </div>
      <ul className="text-foreground space-y-0.5">
        {lines.map((l, n) => <li key={n} className={n === 0 ? 'font-semibold' : ''}>{l}</li>)}
      </ul>
    </div>
  );
}

/** A "?" that opens Why for one team of a row (the Desk drawer, the ticket, Game Detail's book caption). */
export function PenelopeWhyFor({ row, team, teamA, teamB, slot, className, label }: {
  row: PenelopeGame | null | undefined; team: Team; teamA: string; teamB: string; slot?: 'main' | 'aux'; className?: string; label?: ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const live = row?.live?.baseline ?? null;
  if (!live) return null;
  const name = team === 'A' ? teamA : teamB;
  return (
    <span className={cn('relative inline-flex', className)}>
      <button
        type="button"
        aria-label={`Why — ${name}`}
        title={`Why is she doing what she is doing on ${name}?`}
        className="border-border text-muted-foreground hover:text-foreground rounded border px-1 text-[9px] font-bold leading-tight"
        onClick={(e) => { e.stopPropagation(); setOpen((o) => !o); }}
      >
        {label ?? '?'}
      </button>
      {open && <WhyPanel lines={whyLines({ live, team, teamA, teamB, slot, configVersion: row?.live?.baseline.configVersion })} onClose={() => setOpen(false)} />}
    </span>
  );
}

/** A bot order's chip in the open-orders list that opens Why for THAT order (PEN / MG / VH), found by its order id in the
 *  game's Penelope, mini game or harvester row. With no matching row it is the plain chip. */
export function PenelopeOrderWhy({ game, orderId, children }: { game: GameRow; orderId: string; children: ReactNode }) {
  const access = usePenelopeAccess();
  const sides = usePenelopeSides(game, access);
  const [open, setOpen] = useState(false);
  const rows = [sides.control.game, sides.control.minigame?.game ?? null, sides.control.harvester?.game ?? null];
  let hit: { live: Snapshot; team: Team; version?: number } | null = null;
  for (const r of rows) {
    const live = r?.live?.baseline;
    if (!live) continue;
    for (const team of ['A', 'B'] as const) {
      if (live.rests[team]?.orderId === orderId || live.aux?.[team]?.orderId === orderId) hit = { live, team, version: live.configVersion };
    }
  }
  if (!hit) return <>{children}</>;
  const found = hit;
  return (
    <span className="relative inline-flex">
      <button type="button" aria-label="Why this order" className="cursor-help" onClick={(e) => { e.stopPropagation(); setOpen((o) => !o); }}>
        {children}
      </button>
      {open && <WhyPanel lines={whyLines({ live: found.live, team: found.team, teamA: game.awayCode, teamB: game.homeCode, orderId, configVersion: found.version })} onClose={() => setOpen(false)} />}
    </span>
  );
}
